import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Avatar} from "../../schema";
import SimpleAvatarPlayer from "./SimpleAvatarPlayer";
import {BabylonAvatarPlayerRef} from "./BabylonAvatarPlayer.props";
import cn from "classnames";
import classes from "./AvatarPlayer.module.css";
import BabylonAvatarPlayerLazy from "./BabylonAvatarPlayerLazy";
import {ThreeDimensionsOffIcon, ThreeDimensionsIcon} from "../../ui/icons";
import {analyticsSendEvent} from "../../libs/analytics";
import {useModal} from "../ModalProvider";
import {isWebGLSupported} from "../../utils/isWebGLSupported";
import {openWebGLUnavailableModal} from "./WebGLUnavailableModal";

type PlayerDisplayMode = "2D" | "3D"

type props = React.ComponentProps<"div"> & {
  className?: string
  initialMode: PlayerDisplayMode,
  avatar: Avatar,
  gestures: string[]
  portraitMode?: boolean,
  hideLoaderOnMobile?: boolean,
  hideSwitchOnMobile?: boolean,
  onUnloaded?: () => void,
  onLoaded?: () => void,
}

export type AvatarPlayerRef = {
  switchMode: () => void,
  mode: PlayerDisplayMode,
  resize: () => void
}

const AvatarPlayer = forwardRef<AvatarPlayerRef, props>(function ({
  className,
  initialMode,
  avatar,
  gestures,
  portraitMode,
  hideLoaderOnMobile,
  hideSwitchOnMobile,
  onLoaded,
  onUnloaded,
  ...props
}, ref) {
  const {t} = useTranslation();
  const {add: addModal} = useModal();

  const [mode, setMode] = useState<PlayerDisplayMode>(() =>
    initialMode === "3D" && !isWebGLSupported() ? "2D" : initialMode
  );
  const [isLoaded, setLoaded] = useState(() =>
    initialMode === "2D" || (initialMode === "3D" && !isWebGLSupported())
  );
  const babylonPlayerRef = React.useRef<BabylonAvatarPlayerRef>(null);
  const pending2DFallbackPlaybackRef = useRef(false);
  const showedInitialWebglModalRef = useRef(false);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const handleLoaded = React.useCallback(() => {
    setLoaded(true);
    onLoaded && onLoaded();
  }, [onLoaded]);

  const switchMode = useCallback((e?: React.SyntheticEvent) => {
    const targetClassname = e?.currentTarget?.className;
    const currentMode = modeRef.current;
    const nextMode = currentMode === "3D" ? "2D" : "3D";
    if (nextMode === "3D" && !isWebGLSupported()) {
      openWebGLUnavailableModal(addModal, t);
      return;
    }
    setLoaded(false);
    onUnloaded && onUnloaded();
    analyticsSendEvent("avatarPlayerSwitchMode", {
      switchTo: nextMode,
      toggleClassname: targetClassname
    });
    setMode(nextMode);
  }, [onUnloaded, addModal, t]);

  const handle3DSceneLoadFail = useCallback(() => {
    pending2DFallbackPlaybackRef.current = true;
    switchMode();
  }, [switchMode])

  const handle3DEngineInitFail = useCallback(() => {
    openWebGLUnavailableModal(addModal, t);
    pending2DFallbackPlaybackRef.current = true;
    switchMode();
  }, [switchMode, addModal, t]);

  useEffect(() => {
    if (initialMode !== "3D" || isWebGLSupported() || showedInitialWebglModalRef.current) {
      return;
    }
    showedInitialWebglModalRef.current = true;
    openWebGLUnavailableModal(addModal, t);
  }, [initialMode, addModal, t]);

  useImperativeHandle(ref, () => ({
    switchMode, mode,
    resize: () => {
      if (babylonPlayerRef.current) {
        babylonPlayerRef.current.resize();
      }
    }
  }), [mode, switchMode]);

  return (
    <>
      {mode === "2D" && (
        <SimpleAvatarPlayer
          className={className}
          avatar={avatar}
          portraitMode={portraitMode}
          onLoaded={handleLoaded}
          {...props}
        />
      )}

      {isLoaded && (
        <div className={classes.switchRoot}>
          <button
            className={cn(classes.switchModeBtn, {[classes.hideOnMobile]: hideSwitchOnMobile})}
            onClick={switchMode}
          >
            {mode === "2D" ? <ThreeDimensionsIcon/> : <ThreeDimensionsOffIcon/>}
          </button>
        </div>
      )}

      {(mode === "3D") && (
        <BabylonAvatarPlayerLazy
          ref={babylonPlayerRef}
          className={className}
          avatar={avatar}
          gestures={gestures}
          portraitMode={portraitMode}
          previewScreen={SimpleAvatarPlayer}
          onLoaded={handleLoaded}
          onLoadCancel={handle3DSceneLoadFail}
          onEngineInitError={handle3DEngineInitFail}
        />
      )}
    </>
  )
});

export default AvatarPlayer;
